import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FaSearch, FaTimes, FaChevronLeft, FaChevronRight, FaExternalLinkAlt, FaTasks, FaClipboardList } from 'react-icons/fa';
import httpService from '../services/httpService';
import PracticeModal from '../components/ListenerComponents/PracticeModal';
import PageHero from '../components/PageHero';
import '../styles/ListenerPractices.scss';

// ─── Calendar helpers ─────────────────────────────────────────────────────────

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function buildCalendarGrid(year, month) {
  const firstDow   = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const day = i - firstDow + 1;
    cells.push(day >= 1 && day <= daysInMonth ? day : null);
  }
  return cells;
}

function practicesForDay(practices, year, month, day) {
  return practices.filter(p => {
    if (!p.practice_date_time) return false;
    const d = new Date(p.practice_date_time);
    return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
  });
}

// ─── Mobile Calendar ──────────────────────────────────────────────────────────

const MobileCalendar = ({ practices, year, month, selectedDay, onSelectDay, onOpenPractice }) => {
  const today      = new Date();
  const cells      = buildCalendarGrid(year, month);
  const dayItems   = selectedDay ? practicesForDay(practices, year, month, selectedDay) : [];

  const dayLabel = selectedDay
    ? new Date(year, month - 1, selectedDay).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <div>
      {/* Compact dot grid */}
      <div className="lp-mcal__grid">
        {DAY_NAMES.map(d => (
          <div key={d} className="lp-mcal__dow">{d[0]}</div>
        ))}
        {cells.map((day, idx) => {
          const dp = day ? practicesForDay(practices, year, month, day) : [];
          const isToday = day
            && today.getFullYear() === year
            && today.getMonth() + 1 === month
            && today.getDate() === day;
          const isSelected = day === selectedDay;

          return (
            <div
              key={idx}
              className={[
                'lp-mcal__cell',
                !day       ? 'lp-mcal__cell--empty'    : '',
                isToday    ? 'lp-mcal__cell--today'    : '',
                isSelected ? 'lp-mcal__cell--selected' : '',
              ].join(' ')}
              onClick={() => day && onSelectDay(day)}
            >
              {day && (
                <>
                  <div className="lp-mcal__num">{day}</div>
                  {dp.length > 0 && (
                    <div className="lp-mcal__dots">
                      {dp.slice(0, 3).map(p => (
                        <span
                          key={p.id}
                          className={`lp-mcal__dot lp-mcal__dot--${p.completed ? 'done' : 'progress'}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Day detail panel */}
      <div className="lp-mcal__panel">
        <div className="lp-mcal__panel-header">
          <span>{dayLabel || 'Tap a day to see practices'}</span>
          {dayItems.length > 0 && (
            <span className="lp-mcal__panel-count">{dayItems.length}</span>
          )}
        </div>

        {dayItems.length === 0 ? (
          <div className="lp-mcal__panel-empty">
            {selectedDay ? 'No practices on this day' : 'Select a day above'}
          </div>
        ) : (
          <div className="lp-mcal__panel-list">
            {dayItems.map(p => (
              <button
                key={p.id}
                className={`lp-mcal__row lp-mcal__row--${p.completed ? 'done' : 'progress'}`}
                onClick={() => onOpenPractice(p.id)}
              >
                <div className="lp-mcal__row-title">{p.title || 'Practice'}</div>
                <div className="lp-mcal__row-status">
                  {p.completed ? '✓ Completed' : '● In Progress'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Calendar Tab ─────────────────────────────────────────────────────────────

const MAX_VISIBLE = 3;

const CalendarTab = ({ onOpenPractice }) => {
  const now = new Date();
  const [year, setYear]         = useState(now.getFullYear());
  const [month, setMonth]       = useState(now.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(now.getDate()); // mobile only
  const [practices, setPractices] = useState([]);
  const [loading, setLoading]     = useState(false);

  const fetchCalendar = useCallback(() => {
    setLoading(true);
    httpService.get('/dash/practices/calendar', { params: { year, month } })
      .then(res => setPractices(res.data.data || []))
      .catch(err => console.error('Error loading calendar:', err))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    setSelectedDay(null);
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    setSelectedDay(null);
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const cells = buildCalendarGrid(year, month);
  const today = new Date();

  return (
    <div className="pt-3">

      {/* Shared month navigation */}
      <div className="lp-cal__nav">
        <Button variant="outline-secondary" size="sm" onClick={prevMonth}>
          <FaChevronLeft />
        </Button>
        <span className="lp-cal__month">{MONTH_NAMES[month - 1]} {year}</span>
        <Button variant="outline-secondary" size="sm" onClick={nextMonth}>
          <FaChevronRight />
        </Button>
      </div>

      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {!loading && (
        <>
          {/* ── Desktop grid (≥ 768px) ── */}
          <div className="d-none d-md-block">
            <div className="lp-cal__grid">
              {DAY_NAMES.map(d => (
                <div key={d} className="lp-cal__dow">{d}</div>
              ))}
              {cells.map((day, idx) => {
                const dayPractices = day ? practicesForDay(practices, year, month, day) : [];
                const isToday = day
                  && today.getFullYear() === year
                  && today.getMonth() + 1 === month
                  && today.getDate() === day;
                const visible  = dayPractices.slice(0, MAX_VISIBLE);
                const overflow = dayPractices.length - MAX_VISIBLE;

                return (
                  <div
                    key={idx}
                    className={[
                      'lp-cal__cell',
                      !day    ? 'lp-cal__cell--empty' : '',
                      isToday ? 'lp-cal__cell--today' : '',
                    ].join(' ')}
                  >
                    {day && (
                      <>
                        <div className={`lp-cal__day-num${isToday ? ' lp-cal__day-num--today' : ''}`}>
                          {day}
                        </div>
                        {visible.map(p => (
                          <OverlayTrigger
                            key={p.id}
                            placement="top"
                            overlay={
                              <Tooltip>
                                <strong>{p.title || 'Practice'}</strong>
                                <div style={{ fontSize: '0.75em', opacity: 0.8 }}>
                                  {p.completed ? 'Completed' : 'In Progress'}
                                </div>
                              </Tooltip>
                            }
                          >
                            <button
                              className={`lp-cal__pill lp-cal__pill--${p.completed ? 'done' : 'progress'}`}
                              onClick={() => onOpenPractice(p.id)}
                            >
                              {p.title || 'Practice'}
                            </button>
                          </OverlayTrigger>
                        ))}
                        {overflow > 0 && (
                          <div className="lp-cal__overflow">+{overflow} more</div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Mobile grid + day panel (< 768px) ── */}
          <div className="d-md-none">
            <MobileCalendar
              practices={practices}
              year={year}
              month={month}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              onOpenPractice={onOpenPractice}
            />
          </div>
        </>
      )}
    </div>
  );
};

// ─── Practice List Item ───────────────────────────────────────────────────────

const PracticeListItem = ({ practice, onOpenPractice }) => {
  const navigate = useNavigate();
  const club    = practice.key_takeaway?.podcast_club;
  const episode = practice.key_takeaway?.podcast_episode;

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className={`lp-practice-item card${practice.completed ? ' lp-practice-item--done' : ' lp-practice-item--progress'}`}>
      <div className="card-body p-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="flex-grow-1 overflow-hidden">

            {/* Breadcrumb */}
            {(club || episode || practice.key_takeaway) && (
              <div className="lp-practice-item__breadcrumb">
                {club    && <span>{club.title}</span>}
                {episode && <><span style={{ opacity: 0.4 }}>›</span><span>{episode.title}</span></>}
                {practice.key_takeaway && <><span style={{ opacity: 0.4 }}>›</span><span>{practice.key_takeaway.title}</span></>}
              </div>
            )}

            {/* Title + status badge */}
            <div className="d-flex align-items-center gap-2">
              <div className="lp-practice-item__title">{practice.title || 'Practice'}</div>
              <Badge
                bg={practice.completed ? 'success' : 'secondary'}
                style={{ fontSize: '0.62rem', flexShrink: 0 }}
              >
                {practice.completed ? 'Completed' : 'In Progress'}
              </Badge>
            </div>

            {/* Date */}
            {practice.practice_date_time && (
              <div className="lp-practice-item__date">{fmtDate(practice.practice_date_time)}</div>
            )}
          </div>

          {/* Action buttons */}
          <div className="d-flex gap-1 flex-shrink-0">
            <OverlayTrigger placement="top" overlay={<Tooltip>View Practice</Tooltip>}>
              <Button size="sm" variant="outline-primary" onClick={() => onOpenPractice(practice.id)}>
                <FaTasks />
              </Button>
            </OverlayTrigger>
            {club?.guid && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Go to Club</Tooltip>}>
                <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/listener-podcast-club/${club.guid}`)}>
                  <FaExternalLinkAlt />
                </Button>
              </OverlayTrigger>
            )}
            {club?.id && episode?.id && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Go to Episode</Tooltip>}>
                <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/listener-episode/${club.id}/${episode.id}`)}>
                  <FaExternalLinkAlt />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Practices Status Pane ────────────────────────────────────────────────────
// Renders one status tab's paginated list (overdue / this_week / upcoming /
// completed). Fetching + pagination live here per-tab so switching tabs never
// has to load the listener's whole practice history into memory client-side.

const PracticesStatusPane = ({ status, search, episodeFilter, keyTakeawayFilter, pageSize, onOpenPractice }) => {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNextPage: false });

  useEffect(() => {
    setLoading(true);
    httpService.get('/dash/practices', {
      params: {
        status,
        pageNumber,
        pageSize,
        ...(search ? { search } : {}),
        ...(episodeFilter ? { episode_id: episodeFilter } : {}),
        ...(keyTakeawayFilter ? { key_takeaway_id: keyTakeawayFilter } : {}),
      },
    })
      .then(res => {
        setPractices(res.data.data || []);
        setPagination({
          total:      res.data.total      || 0,
          totalPages: res.data.totalPages || 1,
          hasNextPage: res.data.hasNextPage || false,
        });
      })
      .catch(err => console.error('Error loading practices:', err))
      .finally(() => setLoading(false));
  }, [status, search, episodeFilter, keyTakeawayFilter, pageSize, pageNumber]);

  return (
    <div className="pt-3">
      {loading && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}

      {!loading && practices.length === 0 && (
        <p className="text-muted text-center py-4">No practices found.</p>
      )}

      {!loading && practices.map(p => (
        <PracticeListItem key={p.id} practice={p} onOpenPractice={onOpenPractice} />
      ))}

      {!loading && (
        <div className="d-flex align-items-center justify-content-between mt-3">
          <small className="text-muted">{pagination.total} practice{pagination.total !== 1 ? 's' : ''}</small>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-primary" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(p => p - 1)}>
              <FaChevronLeft />
            </Button>
            <small>Page {pageNumber} of {pagination.totalPages}</small>
            <Button variant="outline-primary" size="sm" disabled={!pagination.hasNextPage} onClick={() => setPageNumber(p => p + 1)}>
              <FaChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── List Tab ─────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'overdue',   label: 'Overdue' },
  { key: 'this_week', label: 'This Week' },
  { key: 'upcoming',  label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
];

const ListTab = ({ onOpenPractice }) => {
  const [search]         = useState(''); // search UI currently disabled below
  const [pageSize, setPageSize] = useState(15);
  const [statusTab, setStatusTab] = useState('overdue');
  const [counts, setCounts]       = useState({});

  // ── Filters: by episode, by key takeaway / insight ──
  const [episodeFilter, setEpisodeFilter]         = useState('');
  const [keyTakeawayFilter, setKeyTakeawayFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ episodes: [], key_takeaways: [] });

  useEffect(() => {
    httpService.get('/dash/practices/filters')
      .then(res => setFilterOptions(res.data.data || { episodes: [], key_takeaways: [] }))
      .catch(err => console.error('Error loading practice filters:', err));
  }, []);

  // Tab badge counts follow the active search/episode/insight filters.
  useEffect(() => {
    httpService.get('/dash/practices/counts', {
      params: {
        ...(search ? { search } : {}),
        ...(episodeFilter ? { episode_id: episodeFilter } : {}),
        ...(keyTakeawayFilter ? { key_takeaway_id: keyTakeawayFilter } : {}),
      },
    })
      .then(res => setCounts(res.data.data || {}))
      .catch(err => console.error('Error loading practice counts:', err));
  }, [search, episodeFilter, keyTakeawayFilter]);

  // Only show insights that belong to the selected episode (when one is picked).
  const keyTakeawayOptions = episodeFilter
    ? filterOptions.key_takeaways.filter(t => String(t.podcast_episode_id) === String(episodeFilter))
    : filterOptions.key_takeaways;

  const handleEpisodeFilterChange = (e) => {
    const value = e.target.value;
    setEpisodeFilter(value);
    // Selected insight may not belong to the newly chosen episode — reset it.
    setKeyTakeawayFilter('');
  };

  const handleKeyTakeawayFilterChange = (e) => {
    setKeyTakeawayFilter(e.target.value);
  };

  // Forces each status pane to remount (and reset its own page to 1) whenever
  // a shared filter changes, instead of juggling that reset by hand per pane.
  const paneFilterKey = `${search}|${episodeFilter}|${keyTakeawayFilter}|${pageSize}`;

  return (
    <div className="pt-3">
      <Row className="mb-3 align-items-center">
        <Col md={12} className="mb-2">
          <Row className="g-2">
            <Col md={6}>
              <Form.Select
                size="sm"
                value={episodeFilter}
                onChange={handleEpisodeFilterChange}
              >
                <option value="">All episodes</option>
                {filterOptions.episodes.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.title}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Select
                size="sm"
                value={keyTakeawayFilter}
                onChange={handleKeyTakeawayFilterChange}
              >
                <option value="">All insights</option>
                {keyTakeawayOptions.map(t => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Col>
        <Col md={7}>
          {/*<Form onSubmit={handleSearchSubmit}>*/}
          {/*  <div className="d-flex gap-2">*/}
          {/*    <InputGroup size="sm">*/}
          {/*      <Form.Control*/}
          {/*        type="text"*/}
          {/*        placeholder="Search practices, clubs, episodes..."*/}
          {/*        value={search}*/}
          {/*        onChange={e => setSearch(e.target.value)}*/}
          {/*      />*/}
          {/*      <Button variant="primary" type="submit"><FaSearch /></Button>*/}
          {/*    </InputGroup>*/}
          {/*    {search && (*/}
          {/*      <Button variant="outline-secondary" size="sm" onClick={handleClearSearch}>*/}
          {/*        <FaTimes />*/}
          {/*      </Button>*/}
          {/*    )}*/}
          {/*  </div>*/}
          {/*</Form>*/}
        </Col>
        <Col md={5} className="d-flex align-items-center justify-content-end gap-2">
          <Form.Label className="mb-0 small">Per page:</Form.Label>
          <Form.Select size="sm" value={pageSize} style={{ width: 'auto' }}
            onChange={e => setPageSize(parseInt(e.target.value))}>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="25">25</option>
          </Form.Select>
        </Col>
      </Row>

      <Tabs activeKey={statusTab} onSelect={(k) => setStatusTab(k)} className="mb-2" mountOnEnter>
        {STATUS_TABS.map(tab => (
          <Tab
            key={tab.key}
            eventKey={tab.key}
            title={<span>{tab.label} <Badge bg="secondary" className="ms-1">{counts[tab.key] ?? 0}</Badge></span>}
          >
            <PracticesStatusPane
              key={`${tab.key}-${paneFilterKey}`}
              status={tab.key}
              search={search}
              episodeFilter={episodeFilter}
              keyTakeawayFilter={keyTakeawayFilter}
              pageSize={pageSize}
              onOpenPractice={onOpenPractice}
            />
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ListenerPractices = () => {
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [showModal, setShowModal]   = useState(false);

  const openPractice = (id) => {
    setSelectedPracticeId(id);
    setShowModal(true);
  };

  return (
    <div id="ListenerPractices-component">

      <PageHero>
        <h1 className="page-hero__title">
          <FaClipboardList className="me-2" style={{ opacity: 0.75 }} />
          My Practices
        </h1>
        <p className="page-hero__sub">Track and review your learning practices across all clubs. Everywhere you've tapped "I tried this" across all the clubs you subscribed to, in one place. Private to you by default.</p>
      </PageHero>

      <div className="container-lg py-4 px-3 px-lg-4">
        <Tabs defaultActiveKey="list" className="mb-2">
          <Tab eventKey="list" title={<span><FaClipboardList className="me-1" />List</span>}>
            <ListTab onOpenPractice={openPractice} />
          </Tab>
          <Tab eventKey="calendar" title={<span><FaChevronRight className="me-1" />Calendar</span>}>
            <CalendarTab onOpenPractice={openPractice} />
          </Tab>
        </Tabs>
      </div>

      <PracticeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        practice_id={selectedPracticeId}
      />
    </div>
  );
};

export default ListenerPractices;
